import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Camera,
  CameraOff,
  ChevronDown,
  Copy,
  Hand,
  Link2,
  LogOut,
  MessageCircle,
  Mic,
  MicOff,
  MonitorUp,
  Radio,
  Settings,
  ShieldCheck,
  Smile,
  Users,
  Video,
} from "lucide-react";
import {
  ExternalE2EEKeyProvider,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHAT_TOPIC = "nation-chat-v1";
const KEY_TOPIC = "nation-key-exchange-v1";
const REACTION_TOPIC = "nation-reaction-v1";
const HAND_TOPIC = "nation-hand-v1";
const PROFILE_TOPIC = "nation-profile-v1";
const VAULT_DB = "nation-communications-vault";
const VAULT_STORE = "room-keys";
const DEVICE_KEY_ID = "device-wrap-key";
const COAT_OF_ARMS =
  "https://customer-assets.emergentagent.com/job_92e13f48-99c4-452a-b014-1be6a3b8b28b/artifacts/e3352bxk_Mock%20up%20Jonesaica%20Yah%E2%80%99shar%E2%80%99al.png";

function b64url(bytes) {
  let binary = "";
  for (const b of new Uint8Array(bytes)) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function fromB64url(value) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

function hashParams() {
  return new URLSearchParams(window.location.hash.replace(/^#/, ""));
}

function readFragmentKey() {
  const value = hashParams().get("key");
  if (!value) return null;
  const bytes = fromB64url(value);
  return bytes.length === 32 ? bytes : null;
}

function readGuestInviteCode() {
  return hashParams().get("invite") || "";
}

function openVault() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(VAULT_DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(VAULT_STORE)) req.result.createObjectStore(VAULT_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function vaultGet(key) {
  const db = await openVault();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VAULT_STORE, "readonly");
    const req = tx.objectStore(VAULT_STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function vaultPut(key, value) {
  const db = await openVault();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VAULT_STORE, "readwrite");
    tx.objectStore(VAULT_STORE).put(value, key);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function getDeviceWrapKey() {
  let key = await vaultGet(DEVICE_KEY_ID);
  if (key) return key;
  key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  await vaultPut(DEVICE_KEY_ID, key);
  return key;
}

async function rememberRoomKey(roomId, roomKey) {
  const wrapKey = await getDeviceWrapKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: new TextEncoder().encode(`nation-room-vault|${roomId}`) },
    wrapKey,
    roomKey
  );
  await vaultPut(`room:${roomId}`, { iv: b64url(iv), ciphertext: b64url(ciphertext) });
}

async function loadRememberedRoomKey(roomId) {
  const record = await vaultGet(`room:${roomId}`);
  if (!record) return null;
  try {
    const wrapKey = await getDeviceWrapKey();
    const plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: fromB64url(record.iv),
        additionalData: new TextEncoder().encode(`nation-room-vault|${roomId}`),
      },
      wrapKey,
      fromB64url(record.ciphertext)
    );
    return new Uint8Array(plaintext);
  } catch {
    return null;
  }
}

async function packetKey(rawKey) {
  return crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

async function encryptPacket(rawKey, roomId, topic, value) {
  const key = await packetKey(rawKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: new TextEncoder().encode(`${roomId}|${topic}`),
    },
    key,
    new TextEncoder().encode(JSON.stringify(value))
  );
  return new TextEncoder().encode(JSON.stringify({ iv: b64url(iv), ciphertext: b64url(ciphertext) }));
}

async function decryptPacket(rawKey, roomId, topic, packet) {
  const envelope = JSON.parse(new TextDecoder().decode(packet));
  const key = await packetKey(rawKey);
  const plaintext = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: fromB64url(envelope.iv),
      additionalData: new TextEncoder().encode(`${roomId}|${topic}`),
    },
    key,
    fromB64url(envelope.ciphertext)
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
}

async function exportX25519(publicKey) {
  return b64url(await crypto.subtle.exportKey("raw", publicKey));
}

async function importX25519(value) {
  return crypto.subtle.importKey("raw", fromB64url(value), { name: "X25519" }, false, []);
}

async function deriveWrapKey(privateKey, publicKey, roomId) {
  const bits = await crypto.subtle.deriveBits({ name: "X25519", public: publicKey }, privateKey, 256);
  const material = await crypto.subtle.importKey("raw", bits, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new TextEncoder().encode(roomId),
      info: new TextEncoder().encode("nation-room-key-wrap-v1"),
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

function MediaView({ publication, muted = false, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const track = publication?.track;
    const el = ref.current;
    if (!track || !el) return undefined;
    let stopped = false;
    let raf = 0;
    track.attach(el);
    el.muted = muted;
    el.playsInline = true;
    const wake = () => {
      if (stopped) return;
      el.play?.().catch(() => {});
      raf = requestAnimationFrame(wake);
    };
    const onVisible = () => { if (!document.hidden) el.play?.().catch(() => {}); };
    const timers = [80, 300, 900].map((ms) => setTimeout(() => el.play?.().catch(() => {}), ms));
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    wake();
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
      try { track.detach(el); } catch {}
    };
  }, [publication, muted]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} />;
}

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";
}

function Avatar({ name, mode = "initials", avatarUrl, className = "" }) {
  if ((mode === "avatar" || mode === "custom") && avatarUrl) {
    return <img src={avatarUrl} alt={`${name || "Participant"} avatar`} className={`object-cover rounded-full ${className}`} />;
  }
  if (mode === "nation") {
    return <img src={COAT_OF_ARMS} alt="" className={`object-cover rounded-full ${className}`} />;
  }
  return (
    <div className={`rounded-full bg-[#173d78] text-white flex items-center justify-center font-bold ${className}`}>
      {initials(name)}
    </div>
  );
}

function ParticipantRailCard({ participant, active, onSelect, raised, profile, speaking }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative flex-shrink-0 w-24 sm:w-32 rounded-lg border overflow-hidden text-left ${
        speaking ? "border-emerald-400 ring-2 ring-emerald-400/45" : active ? "border-kingdom-gold ring-2 ring-kingdom-gold/40" : "border-slate-700"
      }`}
    >
      <div className="h-14 sm:h-20 bg-slate-900">
        {participant.camera?.track && !participant.camera?.isMuted ? (
          <MediaView publication={participant.camera} muted={participant.isLocal} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Avatar name={participant.name} mode={profile?.avatar_mode} avatarUrl={profile?.avatar_url} className={`w-14 h-14 text-lg transition-all ${speaking ? "ring-4 ring-emerald-400 ring-offset-2 ring-offset-slate-900" : ""}`} />
          </div>
        )}
      </div>
      <div className="bg-slate-900 px-2 py-1.5 text-[11px] text-white">
        <div className="truncate font-semibold">{participant.name}</div>
        {profile?.headline && <div className="truncate text-slate-500 mt-0.5">{profile.headline}</div>}
      </div>
      {speaking && <div className="absolute top-2 left-2 rounded-full bg-emerald-400/95 text-slate-950 px-2 py-1 text-[10px] font-bold tracking-wider shadow animate-pulse">▮ ▮▮ TALKING</div>}
      {speaking && <div className="absolute top-2 left-2 rounded-full bg-emerald-400/95 text-slate-950 px-2 py-1 text-[10px] font-bold tracking-wider shadow animate-pulse">▮ ▮▮ TALKING</div>}
      {raised && (
        <div className="absolute top-2 right-2 rounded-full bg-amber-400 text-slate-950 p-1 shadow">
          <Hand className="w-4 h-4" />
        </div>
      )}
    </button>
  );
}

function PrejoinLobby({
  roomTitle,
  displayName,
  setDisplayName,
  headline,
  setHeadline,
  avatarMode,
  setAvatarMode,
  previewStream,
  previewVideoRef,
  micEnabled,
  setMicEnabled,
  cameraEnabled,
  setCameraEnabled,
  devices,
  micDeviceId,
  setMicDeviceId,
  cameraDeviceId,
  setCameraDeviceId,
  micLevel,
  permissionsError,
  requestDevices,
  joining,
  onJoin,
  onBack,
  guestMode,
  accountAvatarUrl,
  avatarUploading,
  onAvatarFile,
}) {
  return (
    <div className="min-h-screen bg-[#0b0d11] text-white">
      <header className="h-16 border-b border-slate-800 px-5 flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3">
          <img src={COAT_OF_ARMS} alt="Nation coat of arms" className="w-10 h-10 rounded-full object-cover" />
          <div className="text-right">
            <div className="font-semibold">Yasharal Live Meeting</div>
            <div className="text-[11px] text-slate-500">Secure pre-join setup</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-7">
          <h1 className="text-3xl font-semibold">Set up before you enter</h1>
          <p className="text-slate-400 mt-2">{roomTitle}</p>
          <p className="text-sm text-slate-500 mt-1">Entering the room does not start a broadcast.</p>
        </div>

        <div className="grid lg:grid-cols-[1.35fr_.85fr] gap-6">
          <div className="rounded-2xl border border-slate-700 bg-[#111318] overflow-hidden">
            <div className="aspect-video bg-black relative">
              {previewStream && cameraEnabled ? (
                <video ref={previewVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <Avatar name={displayName} mode={avatarMode} avatarUrl={accountAvatarUrl} className="w-28 h-28 text-3xl" />
                  <div className="text-lg">{displayName || "Your display name"}</div>
                </div>
              )}
              <div className="absolute bottom-3 left-3 rounded-full bg-black/65 px-3 py-1.5 text-sm">
                {displayName || "You"}
              </div>
            </div>

            <div className="p-4 flex flex-wrap items-center justify-center gap-3 border-t border-slate-800">
              <Button
                variant="outline"
                className="border-slate-600 text-white"
                onClick={() => setMicEnabled((value) => !value)}
              >
                {micEnabled ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}
                {micEnabled ? "Mute" : "Unmute"}
              </Button>
              <Button
                variant="outline"
                className="border-slate-600 text-white"
                onClick={() => setCameraEnabled((value) => !value)}
              >
                {cameraEnabled ? <Camera className="w-4 h-4 mr-2" /> : <CameraOff className="w-4 h-4 mr-2" />}
                {cameraEnabled ? "Stop camera" : "Start camera"}
              </Button>
              <Button
                variant="outline"
                className="border-slate-600 text-white"
                onClick={requestDevices}
              >
                <Settings className="w-4 h-4 mr-2" /> Check devices
              </Button>
            </div>

            <div className="px-5 pb-5">
              <div className="flex items-center gap-3 text-sm">
                <Mic className="w-4 h-4 text-slate-400" />
                <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full bg-green-400 transition-all" style={{ width: `${Math.min(100, micLevel * 100)}%` }} />
                </div>
                <span className="text-xs text-slate-500 w-20">Mic check</span>
              </div>
              {permissionsError && <p className="text-sm text-amber-300 mt-3">{permissionsError}</p>}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-700 bg-[#111318] p-5 space-y-5">
            <div>
              <label className="text-sm font-semibold">Display name</label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-2 bg-[#0b0d11] border-slate-700 text-white"
                maxLength={80}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Headline <span className="text-slate-500 font-normal">(optional)</span></label>
              <Input
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="Role, function, topic, etc."
                className="mt-2 bg-[#0b0d11] border-slate-700 text-white"
                maxLength={120}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Avatar when camera is off</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setAvatarMode("initials")}
                  className={`rounded-xl border p-3 text-sm ${avatarMode === "initials" ? "border-[#8bb6ff] bg-[#183c73]" : "border-slate-700"}`}
                >
                  <Avatar name={displayName} className="w-12 h-12 mx-auto mb-2" />
                  Initials
                </button>
                <button
                  type="button"
                  onClick={() => setAvatarMode("nation")}
                  className={`rounded-xl border p-3 text-sm ${avatarMode === "nation" ? "border-[#8bb6ff] bg-[#183c73]" : "border-slate-700"}`}
                >
                  <Avatar name={displayName} mode="nation" className="w-12 h-12 mx-auto mb-2" />
                  Nation crest
                </button>
                <div className={`rounded-xl border p-3 text-sm text-center ${avatarMode === "custom" ? "border-[#8bb6ff] bg-[#183c73]" : "border-slate-700"}`}>
                  <button type="button" disabled={!accountAvatarUrl} onClick={() => setAvatarMode("custom")} className="w-full disabled:opacity-60">
                    <Avatar name={displayName} mode="custom" avatarUrl={accountAvatarUrl} className="w-12 h-12 mx-auto mb-2" />
                    {accountAvatarUrl ? (avatarMode === "custom" ? "Using saved avatar" : "Use saved avatar") : "No saved avatar"}
                  </button>
                  <label className="mt-2 inline-block cursor-pointer rounded-md border border-slate-600 px-2 py-1.5 text-xs hover:bg-white/5">
                    {avatarUploading ? "Uploading…" : "Change avatar"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" disabled={avatarUploading} className="hidden" onChange={(event) => { const file = event.target.files?.[0]; if (file) onAvatarFile(file); event.target.value = ""; }} />
                  </label>
                </div>
              </div>
            </div>

            <div className="grid gap-3">
              <div>
                <label className="text-xs text-slate-400">Microphone</label>
                <select
                  value={micDeviceId}
                  onChange={(e) => setMicDeviceId(e.target.value)}
                  className="mt-1 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2 text-sm"
                >
                  <option value="">Default microphone</option>
                  {devices.audioinput.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || "Microphone"}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Camera</label>
                <select
                  value={cameraDeviceId}
                  onChange={(e) => setCameraDeviceId(e.target.value)}
                  className="mt-1 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2 text-sm"
                >
                  <option value="">Default camera</option>
                  {devices.videoinput.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || "Camera"}</option>)}
                </select>
              </div>
            </div>

            <Button
              onClick={onJoin}
              disabled={joining || !displayName.trim()}
              className="w-full bg-[#8bb6ff] text-slate-950 hover:bg-[#9dc1ff] h-12 text-base"
            >
              <Video className="w-4 h-4 mr-2" /> {joining ? "Entering…" : guestMode ? "Enter as guest" : "Enter Nation session"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function NationCommunications() {
  const navigate = useNavigate();
  const location = useLocation();
  const { roomId } = useParams();
  const { user } = useAuth();
  const guestMode = location.pathname.includes("/guest/");

  const [roomTitle, setRoomTitle] = useState("Nation Session");
  const [roomMeta, setRoomMeta] = useState(null);
  const [guestCode] = useState(readGuestInviteCode());
  const [displayName, setDisplayName] = useState("");
  const [headline, setHeadline] = useState("");
  const accountAvatarUrl = user?.avatar_url || user?.avatar || user?.profile_picture_url || user?.profile_image_url || "";
  const [avatarMode, setAvatarMode] = useState("initials");
  const [savedAvatarUrl, setSavedAvatarUrl] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const selectedAvatarUrl = savedAvatarUrl || accountAvatarUrl;
  useEffect(() => {
    if (guestMode || !user) return;
    let mounted = true;
    const loadSavedAvatar = () => {
      axios.get(`${API}/communications/avatar/profile`).then(({ data }) => {
        if (!mounted) return;
        if (data?.url) { setSavedAvatarUrl(data.url); setAvatarMode("custom"); }
        else if (accountAvatarUrl) setAvatarMode((mode) => mode === "initials" ? "custom" : mode);
      }).catch(() => { if (mounted && accountAvatarUrl) setAvatarMode((mode) => mode === "initials" ? "custom" : mode); });
    };
    const onVisible = () => { if (!document.hidden) loadSavedAvatar(); };
    loadSavedAvatar();
    window.addEventListener("pageshow", loadSavedAvatar);
    window.addEventListener("focus", loadSavedAvatar);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted = false;
      window.removeEventListener("pageshow", loadSavedAvatar);
      window.removeEventListener("focus", loadSavedAvatar);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [guestMode, user, accountAvatarUrl]);
  const [connected, setConnected] = useState(false);
  const [joining, setJoining] = useState(false);
  const [secureReady, setSecureReady] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [screenOn, setScreenOn] = useState(false);
  const [participantsTick, setParticipantsTick] = useState(0);
  const [spotlightId, setSpotlightId] = useState(null);
  const [chatText, setChatText] = useState("");
  const [messages, setMessages] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [raisedHands, setRaisedHands] = useState({});
  const [handRaised, setHandRaised] = useState(false);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const [reactionPulse, setReactionPulse] = useState(null);
  const [stageReactions, setStageReactions] = useState([]);
  const [reactionMenu, setReactionMenu] = useState(false);

  const [previewStream, setPreviewStream] = useState(null);
  const previewVideoRef = useRef(null);
  const previewStreamRef = useRef(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [devices, setDevices] = useState({ audioinput: [], videoinput: [] });
  const [micDeviceId, setMicDeviceId] = useState("");
  const [cameraDeviceId, setCameraDeviceId] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const [permissionsError, setPermissionsError] = useState("");

  const roomRef = useRef(null);
  const cameraTransitionRef = useRef(false);
  const [cameraTransitioning, setCameraTransitioning] = useState(false);
  const keyProviderRef = useRef(null);
  const roomKeyRef = useRef(null);
  const pendingKeyPairRef = useRef(null);
  const audioElementsRef = useRef(new Map());
  const meterAnimationRef = useRef(null);
  const meterContextRef = useRef(null);

  const canOpenStudio = Boolean(!guestMode && (roomMeta?.is_host || roomMeta?.can_open_studio));

  const copyParticipantInvite = async () => {
    if (!roomKeyRef.current) return toast.error("Wait for the secure room key before copying an invite.");
    try {
      const response = await axios.post(`${API}/communications/rooms/${roomId}/guest-invite`);
      const fragment = new URLSearchParams({ invite: response.data.invite_code, key: b64url(roomKeyRef.current) }).toString();
      const url = `${window.location.origin}/yasharal-live/guest/${roomId}#${fragment}`;
      await navigator.clipboard.writeText(url);
      toast.success("Meeting-only guest link copied. No account is required.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create participant invite.");
    }
  };

  useEffect(() => {
    if (!guestMode && user && !displayName) {
      setDisplayName(user.display_name || user.username || "");
    }
  }, [guestMode, user]);

  useEffect(() => {
    if (!roomId) return;
    const endpoint = guestMode
      ? `${API}/communications/public/rooms/${encodeURIComponent(roomId)}`
      : `${API}/communications/rooms/${encodeURIComponent(roomId)}`;
    axios.get(endpoint)
      .then((res) => {
        setRoomMeta(res.data || {});
        setRoomTitle(res.data?.title || "Nation Session");
      })
      .catch((error) => {
        if (!guestMode && error?.response?.status === 401) { const next=`${location.pathname}${location.search}${location.hash}`; navigate(`/yasharal-live/login?next=${encodeURIComponent(next)}`); }
      });
  }, [roomId, guestMode, navigate]);

  const stopPreview = () => {
    previewStreamRef.current?.getTracks().forEach((track) => track.stop());
    previewStreamRef.current = null;
    setPreviewStream(null);
    if (meterAnimationRef.current) cancelAnimationFrame(meterAnimationRef.current);
    try { meterContextRef.current?.close(); } catch {}
    meterContextRef.current = null;
    setMicLevel(0);
  };

  const requestDevices = async () => {
    setPermissionsError("");
    stopPreview();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: micEnabled ? (micDeviceId ? { deviceId: { exact: micDeviceId } } : true) : false,
        video: cameraEnabled ? (cameraDeviceId ? { deviceId: { exact: cameraDeviceId } } : true) : false,
      });
      previewStreamRef.current = stream;
      setPreviewStream(stream);
      if (previewVideoRef.current) previewVideoRef.current.srcObject = stream;

      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        audioinput: list.filter((d) => d.kind === "audioinput"),
        videoinput: list.filter((d) => d.kind === "videoinput"),
      });

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          meterContextRef.current = ctx;
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
          source.connect(analyser);
          const data = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            analyser.getByteFrequencyData(data);
            const avg = data.reduce((sum, value) => sum + value, 0) / (data.length * 255);
            setMicLevel(avg * 3.2);
            meterAnimationRef.current = requestAnimationFrame(tick);
          };
          tick();
        }
      }
    } catch (error) {
      setPermissionsError(
        error?.name === "NotAllowedError"
          ? "Camera/microphone permission was blocked. Use the browser permission control beside the address bar, allow access, then click Check devices again."
          : error?.message || "Could not access the selected camera or microphone."
      );
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        setDevices({
          audioinput: list.filter((d) => d.kind === "audioinput"),
          videoinput: list.filter((d) => d.kind === "videoinput"),
        });
      } catch {}
    }
  };

  useEffect(() => {
    if (!roomId || connected) return undefined;
    requestDevices();
    return () => stopPreview();
  }, [roomId, connected]);

  useEffect(() => {
    const stream = previewStream || previewStreamRef.current;
    if (!stream) return;
    const videoTrack = stream.getVideoTracks?.()[0];
    if (videoTrack) videoTrack.enabled = cameraEnabled;
    if (!cameraEnabled) return;
    const video = previewVideoRef.current;
    if (!video) return;
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.play?.().catch(() => {});
  }, [previewStream, cameraEnabled]);

  useEffect(() => () => {
    roomRef.current?.disconnect();
    stopPreview();
    audioElementsRef.current.forEach((element) => element.remove());
    audioElementsRef.current.clear();
  }, []);

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      toast.error("Choose a JPG, PNG or WebP avatar no larger than 5 MB.");
      return;
    }
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append("room_id", roomId);
      if (guestCode) form.append("invite_code", guestCode);
      form.append("file", file);
      const { data } = await axios.post(`${API}/communications/avatar/upload`, form);
      if (!data?.url) throw new Error("Avatar upload returned no image.");
      setSavedAvatarUrl(data.url);
      setAvatarMode("custom");
    } catch (error) { toast.error(error?.response?.data?.detail || error.message || "Avatar upload failed"); }
    finally { setAvatarUploading(false); }
  };

  const enableSecureMedia = async (lkRoom, roomKey) => {
    await keyProviderRef.current.setKey(roomKey.buffer.slice(roomKey.byteOffset, roomKey.byteOffset + roomKey.byteLength));
    await lkRoom.setE2EEEnabled(true);
    roomKeyRef.current = roomKey;
    setSecureReady(true);

    if (micEnabled) {
      await lkRoom.localParticipant.setMicrophoneEnabled(true, micDeviceId ? { deviceId: micDeviceId } : undefined);
      setMicOn(true);
    }
    if (cameraEnabled) {
      await lkRoom.localParticipant.setCameraEnabled(true, cameraDeviceId ? { deviceId: cameraDeviceId } : undefined);
      const cameraTrack = lkRoom.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (cameraTrack) cameraTrack.stopOnMute = false;
      setCameraOn(true);
    }
    if (roomId && !guestMode) await rememberRoomKey(roomId, roomKey);

    const profile = { headline, avatar_mode: avatarMode, avatar_url: selectedAvatarUrl };
    setProfiles((current) => ({ ...current, [lkRoom.localParticipant.identity]: profile }));
    const payload = await encryptPacket(roomKey, roomId, PROFILE_TOPIC, profile);
    await lkRoom.localParticipant.publishData(payload, { reliable: true, topic: PROFILE_TOPIC });
  };

  const sendKeyRequest = async (lkRoom) => {
    const pair = await crypto.subtle.generateKey({ name: "X25519" }, false, ["deriveBits"]);
    pendingKeyPairRef.current = pair;
    const payload = new TextEncoder().encode(JSON.stringify({
      type: "key-request",
      public_key: await exportX25519(pair.publicKey),
    }));
    await lkRoom.localParticipant.publishData(payload, { reliable: true, topic: KEY_TOPIC });
  };

  const answerKeyRequest = async (lkRoom, participant, requestPayload) => {
    if (!roomKeyRef.current || !roomMeta?.is_host) return;
    const requesterPublic = await importX25519(requestPayload.public_key);
    const hostPair = await crypto.subtle.generateKey({ name: "X25519" }, false, ["deriveBits"]);
    const wrappingKey = await deriveWrapKey(hostPair.privateKey, requesterPublic, roomId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: new TextEncoder().encode(`nation-room-key|${roomId}|${participant.identity}`) },
      wrappingKey,
      roomKeyRef.current
    );
    const response = new TextEncoder().encode(JSON.stringify({
      type: "key-response",
      to: participant.identity,
      host_public_key: await exportX25519(hostPair.publicKey),
      iv: b64url(iv),
      ciphertext: b64url(ciphertext),
    }));
    await lkRoom.localParticipant.publishData(response, { reliable: true, topic: KEY_TOPIC });
  };

  const acceptKeyResponse = async (lkRoom, payload) => {
    if (payload.to !== lkRoom.localParticipant.identity || !pendingKeyPairRef.current) return;
    const hostPublic = await importX25519(payload.host_public_key);
    const wrappingKey = await deriveWrapKey(pendingKeyPairRef.current.privateKey, hostPublic, roomId);
    const plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: fromB64url(payload.iv),
        additionalData: new TextEncoder().encode(`nation-room-key|${roomId}|${lkRoom.localParticipant.identity}`),
      },
      wrappingKey,
      fromB64url(payload.ciphertext)
    );
    await enableSecureMedia(lkRoom, new Uint8Array(plaintext));
    pendingKeyPairRef.current = null;
  };

  const attachRemoteAudio = (track, publication) => {
    if (track.kind !== Track.Kind.Audio) return;
    const element = track.attach();
    element.autoplay = true;
    element.style.display = "none";
    document.body.appendChild(element);
    audioElementsRef.current.set(publication.trackSid, element);
  };

  const detachRemoteAudio = (track, publication) => {
    if (track.kind !== Track.Kind.Audio) return;
    track.detach();
    const element = audioElementsRef.current.get(publication.trackSid);
    if (element) element.remove();
    audioElementsRef.current.delete(publication.trackSid);
  };

  const showStageReaction = (emoji, from) => {
    const id = Date.now() + "-" + Math.random().toString(36).slice(2);
    const lanes = Array.from({ length: 30 }, (_, index) => {
      const col = index % 6;
      const row = Math.floor(index / 6);
      return {
        left: (8 + col * 16) + "%",
        bottom: (12 + row * 15) + "%",
        mobileLeft: (10 + col * 16) + "%",
        mobileBottom: (18 + row * 11) + "%",
      };
    });

    setStageReactions((current) => {
      const occupied = new Set(current.map((item) => item.lane));
      const freeLanes = lanes
        .map((_, index) => index)
        .filter((index) => !occupied.has(index));
      const lane = freeLanes.length
        ? freeLanes[Math.floor(Math.random() * freeLanes.length)]
        : Math.floor(Math.random() * lanes.length);
      return [...current.slice(-29), { id, emoji, from, lane, ...lanes[lane] }];
    });

    window.setTimeout(() => {
      setStageReactions((current) => current.filter((item) => item.id !== id));
    }, 3200);
  };

  const reactionMotionClass = (emoji) => {
    if (emoji === "👏🏾") return "yl-reaction-clap";
    if (emoji === "😂") return "yl-reaction-laugh";
    if (emoji === "🎉" || emoji === "💯") return "yl-reaction-celebrate";
    return "yl-reaction-rise";
  };

  const addFloatingReaction = (emoji, from) => {
    const id = `${Date.now()}-${Math.random()}`;
    setFloatingReactions((current) => [...current, { id, emoji, from }]);
    window.setTimeout(() => setFloatingReactions((current) => current.filter((item) => item.id !== id)), 3500);
  };

  const joinRoom = async () => {
    setJoining(true);
    try {
      const fragmentKey = readFragmentKey();
      const remembered = !fragmentKey && !guestMode ? await loadRememberedRoomKey(roomId) : null;
      let initialKey = fragmentKey || remembered;

      if (!initialKey && roomMeta?.is_host && !guestMode) {
        initialKey = crypto.getRandomValues(new Uint8Array(32));
        await rememberRoomKey(roomId, initialKey);
      }

      const tokenResponse = guestMode
        ? await axios.post(`${API}/communications/guest-token`, {
            room_id: roomId,
            display_name: displayName,
            invite_code: guestCode,
          })
        : await axios.post(`${API}/communications/room-token`, {
            room_id: roomId,
            display_name: displayName,
            headline,
          });

      stopPreview();

      const keyProvider = new ExternalE2EEKeyProvider({ keySize: 256 });
      keyProviderRef.current = keyProvider;
      const e2eeWorker = new Worker(new URL("livekit-client/e2ee-worker", import.meta.url));
      const lkRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        encryption: { keyProvider, worker: e2eeWorker },
      });

      const refresh = () => setParticipantsTick((value) => value + 1);
      lkRoom.on(RoomEvent.ParticipantConnected, refresh);
      lkRoom.on(RoomEvent.ParticipantDisconnected, refresh);
      lkRoom.on(RoomEvent.TrackSubscribed, (track, publication) => {
        attachRemoteAudio(track, publication);
        refresh();
      });
      lkRoom.on(RoomEvent.TrackUnsubscribed, (track, publication) => {
        detachRemoteAudio(track, publication);
        refresh();
      });
      lkRoom.on(RoomEvent.LocalTrackPublished, refresh);
      lkRoom.on(RoomEvent.LocalTrackUnpublished, refresh);
      lkRoom.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        refresh();
        if (speakers?.length) setSpotlightId((current) => current || speakers[0].identity);
      });
      lkRoom.on(RoomEvent.DataReceived, async (packet, participant, _kind, topic) => {
        if (topic === KEY_TOPIC) {
          try {
            const payload = JSON.parse(new TextDecoder().decode(packet));
            if (payload.type === "key-request") await answerKeyRequest(lkRoom, participant, payload);
            if (payload.type === "key-response") await acceptKeyResponse(lkRoom, payload);
          } catch (error) {
            console.warn("Secure room key exchange packet ignored", error);
          }
          return;
        }

        if (!roomKeyRef.current) return;
        try {
          if (topic === CHAT_TOPIC) {
            const decoded = await decryptPacket(roomKeyRef.current, roomId, CHAT_TOPIC, packet);
            setMessages((current) => [...current, {
              ...decoded,
              from: participant?.name || participant?.identity || "National",
            }]);
          } else if (topic === REACTION_TOPIC) {
            const decoded = await decryptPacket(roomKeyRef.current, roomId, REACTION_TOPIC, packet);
            showStageReaction(decoded.emoji, participant?.name || "Participant");
          } else if (topic === HAND_TOPIC) {
            const decoded = await decryptPacket(roomKeyRef.current, roomId, HAND_TOPIC, packet);
            setRaisedHands((current) => ({ ...current, [participant?.identity]: Boolean(decoded.raised) }));
          } else if (topic === PROFILE_TOPIC) {
            const decoded = await decryptPacket(roomKeyRef.current, roomId, PROFILE_TOPIC, packet);
            setProfiles((current) => ({ ...current, [participant?.identity]: decoded }));
          }
        } catch {
          // Ignore unauthenticated/corrupted data packets.
        }
      });
      lkRoom.on(RoomEvent.Disconnected, () => {
        setConnected(false);
        setSecureReady(false);
        window.dispatchEvent(new CustomEvent("communications-session-state", { detail: { active: false, roomId, mode: "meeting" } }));
      });

      await lkRoom.connect(tokenResponse.data.url, tokenResponse.data.token);
      roomRef.current = lkRoom;
      setConnected(true);
      window.dispatchEvent(new CustomEvent("communications-session-state", { detail: { active: true, roomId, mode: "meeting" } }));
      setSpotlightId(lkRoom.localParticipant.identity);

      if (initialKey) await enableSecureMedia(lkRoom, initialKey);
      else await sendKeyRequest(lkRoom);
      refresh();
    } catch (error) {
      console.error("Nation room join failed", error);
      toast.error(error?.response?.data?.detail || error?.message || "Could not join room");
    } finally {
      setJoining(false);
    }
  };

  const toggleMic = async () => {
    if (!roomRef.current || !secureReady) return;
    const next = !micOn;
    await roomRef.current.localParticipant.setMicrophoneEnabled(next, micDeviceId ? { deviceId: micDeviceId } : undefined);
    setMicOn(next);
  };

  const toggleCamera = async () => {
    const room = roomRef.current;
    if (!room || !secureReady || cameraTransitionRef.current) return;
    cameraTransitionRef.current = true;
    setCameraTransitioning(true);
    const next = !cameraOn;
    try {
      // Preserve the acquired capture track while muted; reacquiring hardware can take 20-30 seconds.
      const existingTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (existingTrack) existingTrack.stopOnMute = false;
      const publicationBefore = room.localParticipant.getTrackPublication(Track.Source.Camera);
      // Fast path: mute/unmute the existing publication instead of running the
      // device-creation path again. Reacquire only if the browser ended the track.
      if (publicationBefore?.track?.mediaStreamTrack?.readyState === "live") {
        if (next) await publicationBefore.unmute();
        else await publicationBefore.mute();
      } else {
        await room.localParticipant.setCameraEnabled(next, next && cameraDeviceId ? { deviceId: cameraDeviceId } : undefined);
      }
      const activeTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (activeTrack) activeTrack.stopOnMute = false;
      const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const enabled = Boolean(publication?.track && !publication.isMuted && !publication.track.isMuted);
      if (next && !enabled) throw new Error("Camera did not publish a usable track. Check camera permissions and retry.");
      setCameraOn(enabled);
      setParticipantsTick((value) => value + 1);
    } catch (error) {
      const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
      setCameraOn(Boolean(publication?.track && !publication.isMuted && !publication.track.isMuted));
      setParticipantsTick((value) => value + 1);
      toast.error(error?.message || "Camera change failed. Retry.");
    } finally {
      cameraTransitionRef.current = false;
      setCameraTransitioning(false);
    }
  };

  const toggleScreen = async () => {
    if (!roomRef.current || !secureReady) return;
    const next = !screenOn;
    if (next && !navigator.mediaDevices?.getDisplayMedia) {
      toast.error("This mobile browser does not provide screen sharing. Screen sharing will be available from a supported desktop browser or the Yasharal Live app.");
      return;
    }
    try {
      await roomRef.current.localParticipant.setScreenShareEnabled(next);
      setScreenOn(next);
      setParticipantsTick((value) => value + 1);
    } catch (error) {
      toast.error(error?.message || "Screen sharing could not start in this browser.");
    }
  };

  const leaveRoom = () => {
    window.dispatchEvent(new CustomEvent("communications-session-state", { detail: { active: false, roomId, mode: "meeting" } }));
    roomRef.current?.disconnect();
    roomRef.current = null;
    setConnected(false);
    setSecureReady(false);
    setCameraOn(false);
    setMicOn(false);
    setScreenOn(false);
    navigate("/yasharal-live");
  };

  const sendChat = async () => {
    const text = chatText.trim();
    if (!text || !roomRef.current || !roomKeyRef.current) return;
    const message = { text, sent_at: new Date().toISOString() };
    const payload = await encryptPacket(roomKeyRef.current, roomId, CHAT_TOPIC, message);
    await roomRef.current.localParticipant.publishData(payload, { reliable: true, topic: CHAT_TOPIC });
    setMessages((current) => [...current, { ...message, from: "You" }]);
    setChatText("");
  };

  const sendReaction = async (emoji) => {
    const room = roomRef.current;
    const key = roomKeyRef.current;

    showStageReaction(emoji, "You");
    setReactionMenu(false);

    if (!room || !key) {
      toast.error("Secure reaction channel is not ready yet.");
      return;
    }

    try {
      const payload = await encryptPacket(key, roomId, REACTION_TOPIC, { emoji });
      await room.localParticipant.publishData(payload, { reliable: false, topic: REACTION_TOPIC });
    } catch (error) {
      console.warn("Encrypted meeting reaction could not be sent", error);
      toast.error("Reaction could not be sent to the room.");
    }
  };

  const toggleHand = async () => {
    if (!roomRef.current || !roomKeyRef.current) return;
    const next = !handRaised;
    setHandRaised(next);
    setRaisedHands((current) => ({ ...current, [roomRef.current.localParticipant.identity]: next }));
    const payload = await encryptPacket(roomKeyRef.current, roomId, HAND_TOPIC, { raised: next });
    await roomRef.current.localParticipant.publishData(payload, { reliable: true, topic: HAND_TOPIC });
  };

  const participants = useMemo(() => {
    const room = roomRef.current;
    if (!room) return [];
    const list = [];
    const add = (participant, isLocal = false) => {
      let camera = null;
      let screen = null;
      participant.trackPublications.forEach((publication) => {
        if (publication.source === Track.Source.Camera) camera = publication;
        if (publication.source === Track.Source.ScreenShare) screen = publication;
      });
      list.push({
        identity: participant.identity,
        name: participant.name || participant.identity,
        camera,
        screen,
        speaking: Boolean(participant.isSpeaking),
        isLocal,
      });
    };
    add(room.localParticipant, true);
    room.remoteParticipants.forEach((participant) => add(participant, false));
    return list;
  }, [participantsTick, connected]);

  const spotlight = useMemo(() => {
    if (!participants.length) return null;
    const screenSharer = participants.find((participant) => participant.screen?.track && !participant.screen?.isMuted);
    if (screenSharer) return screenSharer;
    return participants.find((participant) => participant.identity === spotlightId) || participants[0];
  }, [participants, spotlightId]);

  const spotlightPublication = spotlight?.screen?.track && !spotlight.screen?.isMuted ? spotlight.screen : spotlight?.camera?.track && !spotlight.camera?.isMuted ? spotlight.camera : null;

  const copyNationalInvite = async () => {
    const key = roomKeyRef.current || readFragmentKey() || (!guestMode ? await loadRememberedRoomKey(roomId) : null);
    if (!key) {
      toast.error("Secure room key is not available on this device yet.");
      return;
    }
    await navigator.clipboard.writeText(`${window.location.origin}/yasharal-live/rooms/${roomId}#key=${b64url(key)}`);
    toast.success("National secure invite copied");
  };

  const copyGuestInvite = async () => {
    let key = roomKeyRef.current || readFragmentKey() || await loadRememberedRoomKey(roomId);
    if (!key && roomMeta?.is_host) {
      key = crypto.getRandomValues(new Uint8Array(32));
      await rememberRoomKey(roomId, key);
    }
    if (!key) {
      toast.error("Secure room key is not available on this device yet.");
      return;
    }

    try {
      const response = await axios.post(`${API}/communications/rooms/${roomId}/guest-invite`);
      const invite = response.data?.invite_code;
      if (!invite) throw new Error("Guest invite code unavailable");
      const fragment = new URLSearchParams({ key: b64url(key), invite });
      await navigator.clipboard.writeText(`${window.location.origin}/yasharal-live/guest/${roomId}#${fragment.toString()}`);
      toast.success("Guest participation link copied");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || "Could not create guest invite");
    }
  };

  if (!roomId) return null;

  if (!connected) {
    return (
      <PrejoinLobby
        roomTitle={roomTitle}
        displayName={displayName}
        setDisplayName={setDisplayName}
        headline={headline}
        setHeadline={setHeadline}
        avatarMode={avatarMode}
        setAvatarMode={setAvatarMode}
        previewStream={previewStream}
        previewVideoRef={previewVideoRef}
        micEnabled={micEnabled}
        setMicEnabled={setMicEnabled}
        cameraEnabled={cameraEnabled}
        setCameraEnabled={setCameraEnabled}
        devices={devices}
        micDeviceId={micDeviceId}
        setMicDeviceId={setMicDeviceId}
        cameraDeviceId={cameraDeviceId}
        setCameraDeviceId={setCameraDeviceId}
        micLevel={micLevel}
        permissionsError={permissionsError}
        requestDevices={requestDevices}
        joining={joining}
        onJoin={joinRoom}
        onBack={() => navigate("/yasharal-live")}
        guestMode={guestMode}
        accountAvatarUrl={selectedAvatarUrl}
        avatarUploading={avatarUploading}
        onAvatarFile={uploadAvatar}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="nation-communications-room">
      <div data-testid="yl-mobile-shell" className="yl-meeting-shell max-w-[1600px] mx-auto px-4 py-5 space-y-4">
        <div data-testid="yl-mobile-header" className="yl-meeting-header flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/yasharal-live")} className="p-2 rounded-lg border border-slate-700 hover:bg-white/5">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <img src={COAT_OF_ARMS} alt="Nation coat of arms" className="w-11 h-11 rounded-full object-cover" />
            <div>
              <div className="flex items-center gap-2 text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
                <ShieldCheck className="w-4 h-4" /> Secure Nation Meeting
              </div>
              <h1 className="font-heading text-2xl mt-1">{roomTitle}</h1>
              <p className="text-xs text-slate-400 mt-1">
                Host: {roomMeta?.created_by_display_name || roomMeta?.created_by_username || "Session Host"}
                {roomMeta?.visibility ? ` · ${roomMeta.visibility} session` : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!guestMode && roomMeta?.is_host && (
              <>
                <Button variant="outline" onClick={copyNationalInvite} className="border-white/30 text-white"><Copy className="w-4 h-4 mr-2" /> National Invite</Button>
                {roomMeta?.allow_guests && <Button variant="outline" onClick={copyGuestInvite} className="border-white/30 text-white"><Link2 className="w-4 h-4 mr-2" /> Guest Invite</Button>}
              </>
            )}
            <Button variant="outline" onClick={leaveRoom} className="border-red-400 text-red-300"><LogOut className="w-4 h-4 mr-2" /> Leave</Button>
          </div>
        </div>

        <div data-testid="yl-mobile-grid" className="yl-meeting-grid grid xl:grid-cols-[minmax(0,1fr)_340px] gap-4">
          <div data-testid="yl-mobile-main" className="yl-meeting-main space-y-3 min-w-0">
            <div data-testid="yl-mobile-stage" className="yl-meeting-stage relative aspect-video bg-black rounded-xl overflow-hidden border border-slate-800">
              {spotlightPublication?.track ? (
                <MediaView publication={spotlightPublication} muted={spotlight?.isLocal} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                  <Avatar
                    name={spotlight?.name || displayName}
                    mode={profiles[spotlight?.identity]?.avatar_mode || (spotlight?.isLocal ? avatarMode : "initials")}
                    avatarUrl={profiles[spotlight?.identity]?.avatar_url || (spotlight?.isLocal ? selectedAvatarUrl : "")}
                    className={`w-28 h-28 text-3xl mb-4 transition-all ${spotlight?.speaking ? "ring-4 ring-emerald-400 ring-offset-4 ring-offset-black" : ""}`}
                  />
                  <p className="text-lg">{spotlight?.name || "Waiting for presenter"}</p>
                </div>
              )}
              {spotlight && (
                <div className="absolute bottom-3 left-3 bg-black/70 px-3 py-1.5 rounded text-sm">
                  {spotlight.name}{spotlight.screen?.track && !spotlight.screen?.isMuted ? " — sharing screen" : ""}
                  {profiles[spotlight.identity]?.headline && <span className="text-slate-300"> · {profiles[spotlight.identity].headline}</span>}
                </div>
              )}
              {!secureReady && (
                <div className="absolute inset-0 bg-black/75 flex items-center justify-center text-center p-8">
                  <div>
                    <ShieldCheck className="w-10 h-10 text-kingdom-gold mx-auto mb-3" />
                    <p className="font-semibold">Waiting for secure room key from the host…</p>
                    <p className="text-sm text-slate-400 mt-2">Your camera and microphone stay off until E2EE is ready.</p>
                  </div>
                </div>
              )}

              {participants.some((participant) => raisedHands[participant.identity] || (participant.isLocal && handRaised)) && (
                <div
                  data-testid="meeting-raised-hand-stage"
                  className="pointer-events-none absolute top-3 right-3 z-[110] flex max-w-[78%] flex-col items-end gap-2"
                >
                  {participants
                    .filter((participant) => raisedHands[participant.identity] || (participant.isLocal && handRaised))
                    .slice(0, 4)
                    .map((participant) => (
                      <div
                        key={participant.identity}
                        className="flex items-center gap-2 rounded-full border border-amber-300/70 bg-amber-400 px-3 py-2 text-sm font-bold text-slate-950 shadow-2xl"
                      >
                        <Hand className="h-5 w-5" />
                        <span className="truncate">{participant.name || "Participant"} raised hand</span>
                      </div>
                    ))}
                </div>
              )}

              {stageReactions.map((item) => (
                <div
                  key={item.id}
                  data-testid="meeting-stage-reaction"
                  className={"pointer-events-none absolute z-[115] " + reactionMotionClass(item.emoji)}
                  style={{ left: item.left, bottom: item.bottom }}
                  title={item.from}
                >
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="drop-shadow-2xl"
                      style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)", lineHeight: 1 }}
                    >
                      {item.emoji}
                    </div>
                    <div className="max-w-[150px] truncate rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-bold text-white shadow-lg backdrop-blur-sm">
                      {item.from || "Participant"}
                    </div>
                  </div>
                </div>
              ))}

              {reactionPulse && (
                <div
                  data-testid="meeting-reaction-pulse"
                  className="pointer-events-none absolute inset-0 z-[90] flex items-center justify-center"
                >
                  <div className="flex flex-col items-center gap-1 rounded-2xl bg-black/35 px-5 py-3 shadow-2xl backdrop-blur-sm">
                    <div
                      className="leading-none animate-bounce"
                      style={{ fontSize: "clamp(3.5rem, 12vw, 7rem)", lineHeight: 1 }}
                    >
                      {reactionPulse.emoji}
                    </div>
                    <div className="text-xs sm:text-sm font-semibold text-white drop-shadow">
                      {reactionPulse.from || "Participant"}
                    </div>
                  </div>
                </div>
              )}

              {reactionMenu && (
                <div
                  data-testid="meeting-reaction-stage-overlay"
                  className="absolute left-1/2 bottom-3 z-[100] -translate-x-1/2 max-w-[calc(100%-1rem)] rounded-2xl border border-slate-600 bg-slate-900/95 p-2 shadow-2xl backdrop-blur pointer-events-auto overflow-x-auto overflow-y-hidden"
                >
                  <div className="flex items-center gap-1 min-w-max">
                    {["👏🏾","🙌🏾","👍🏾","🫀","🔥","💯","🎉","😂"].map((emoji) => (
                      <button
                        type="button"
                        key={emoji}
                        onClick={(event) => { event.preventDefault(); event.stopPropagation(); sendReaction(emoji); }}
                        style={{ touchAction: "manipulation" }}
                        className="flex-shrink-0 text-2xl sm:text-3xl p-2.5 rounded-xl hover:bg-white/10 active:bg-white/15"
                        aria-label={`Send ${emoji} reaction`}
                      >
                        {emoji}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={(event) => { event.preventDefault(); event.stopPropagation(); setReactionMenu(false); }}
                      style={{ touchAction: "manipulation" }}
                      className="flex-shrink-0 ml-1 w-9 h-9 rounded-full border border-slate-600 text-slate-300 hover:text-white hover:bg-white/10"
                      aria-label="Close reactions"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <div className="pointer-events-none absolute inset-x-0 bottom-16 z-50 h-[58%] overflow-hidden">
                {floatingReactions.map((item, index) => (
                  <div
                    key={item.id}
                    className={`absolute text-4xl ${String(item.emoji).startsWith("👏") ? "yl-reaction-clap" : item.emoji === "😂" ? "yl-reaction-laugh" : item.emoji === "🎉" ? "yl-reaction-celebrate" : "yl-reaction-rise"}`}
                    style={{
                      left: `${12 + ((index * 17) % 76)}%`,
                      bottom: "0%",
                      "--yl-drift": `${((index % 2) ? 1 : -1) * (18 + (index % 4) * 9)}px`,
                    }}
                    title={item.from}
                  >
                    {item.emoji}
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="yl-mobile-participant-rail" className={`yl-participant-rail overflow-x-auto pb-2 ${participants.length <= 1 ? "hidden" : ""}`}>
              <div className="flex gap-2 min-w-max">
                {participants.map((participant) => (
                  <ParticipantRailCard
                    key={participant.identity}
                    participant={participant}
                    active={participant.identity === spotlight?.identity}
                    onSelect={() => setSpotlightId(participant.identity)}
                    raised={raisedHands[participant.identity]}
                    profile={profiles[participant.identity]}
                    speaking={participant.speaking}
                  />
                ))}
              </div>
            </div>

            <div data-testid="yl-mobile-controls" className="yl-meeting-controls sticky bottom-2 z-30 flex flex-wrap gap-2 justify-center bg-slate-900/95 backdrop-blur rounded-lg p-2 sm:p-3 shadow-xl">
              <Button onClick={toggleMic} disabled={!secureReady} variant="outline" className="border-slate-600 text-white">
                {micOn ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}{micOn ? "Mute" : "Unmute"}
              </Button>
              <Button onClick={toggleCamera} disabled={!secureReady || cameraTransitioning} variant="outline" className="border-slate-600 text-white">
                {cameraOn ? <Camera className="w-4 h-4 mr-2" /> : <CameraOff className="w-4 h-4 mr-2" />}{cameraOn ? "Camera Off" : "Camera On"}
              </Button>
              <Button onClick={toggleScreen} disabled={!secureReady} variant="outline" className="border-slate-600 text-white">
                <MonitorUp className="w-4 h-4 mr-2" /> {screenOn ? "Stop Share" : "Share Screen"}
              </Button>
              <Button onClick={toggleHand} disabled={!secureReady} variant="outline" className={handRaised ? "border-amber-400 text-amber-300" : "border-slate-600 text-white"}>
                <Hand className="w-4 h-4 mr-2" /> {handRaised ? "Lower hand" : "Raise hand"}
              </Button>
              <div className="relative">
                <Button onClick={() => setReactionMenu((value) => !value)} disabled={!secureReady} variant="outline" className="border-slate-600 text-white">
                  <Smile className="w-4 h-4 mr-2" /> React <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
                
              </div>
              <label className="inline-flex items-center justify-center rounded-md border border-slate-600 px-4 py-2 text-sm font-medium text-white cursor-pointer hover:bg-white/5">
                {avatarUploading ? "Uploading avatar…" : "Change avatar"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  disabled={avatarUploading}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) uploadAvatar(file);
                    event.target.value = "";
                  }}
                />
              </label>
              {!guestMode && roomMeta?.is_host && (
                <Button variant="outline" className="border-slate-600 text-white" onClick={copyParticipantInvite}>
                  <Link2 className="w-4 h-4 mr-2" /> Participant link
                </Button>
              )}
              {canOpenStudio && (
                <Button variant="outline" className="border-kingdom-gold text-kingdom-gold" onClick={() => navigate(`/yasharal-live/studio/${roomId}${location.hash || ""}`)}>
                  <Radio className="w-4 h-4 mr-2" /> Open Studio
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-kingdom-gold" /><span className="font-semibold">Meeting Chat</span></div>
              <div className="flex flex-col h-[410px]">
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? <p className="text-sm text-slate-500">Encrypted meeting chat appears here.</p> : messages.map((item, index) => (
                    <div key={`${item.sent_at}-${index}`} className="text-sm"><span className="font-semibold text-kingdom-gold">{item.from}: </span><span className="text-slate-100">{item.text}</span></div>
                  ))}
                </div>
                <div className="p-3 border-t border-slate-700 flex gap-2">
                  <textarea value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => {if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendChat();}}} disabled={!secureReady} placeholder={secureReady ? "Message the room…" : "Waiting for secure connection…"} rows={2} className="flex-1 min-h-[42px] max-h-32 resize-y rounded-md bg-slate-950 border border-slate-700 p-2 text-white" />
                  <Button onClick={sendChat} disabled={!secureReady || !chatText.trim()} className="bg-royal-blue text-white">Send</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
